import { useCallback } from 'react';
import {
  DynamicFieldProperty_Enum,
  Template,
  TemplateType_Slug,
} from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { useTranslation } from '@frontend/i18n';
import {
  TEMPLATE_TYPE_LINK_MAP,
  useGetDefaultTemplate,
  IntegratedMessaingTrackingPrefixes,
  UseTemplateFlowResult,
} from '@frontend/integrated-messaging';
import { usePaymentsModalButton } from '@frontend/payments-collection-flow';
import { PaymentsFeatureFlags } from '@frontend/payments-hooks';
import { useAppFlagStore } from '@frontend/shared';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { useTemplateMessagingStore } from '../stores/template-selector-store';
import { UseTemplateFlowArgs } from '../types';

export const useTextToPayTemplateFlow = ({
  groupId,
  personId,
  renderTemplate,
  modalControl,
}: UseTemplateFlowArgs): UseTemplateFlowResult => {
  const { t } = useTranslation('inbox-templates');
  const { disabled, disabledMessage } = usePaymentsModalButton({ personId });
  const getDefaultTemplate = useGetDefaultTemplate({ groupIds: [groupId] });
  const { featureFlags } = useAppFlagStore();
  const lastUsedPaymentsTemplate = getDefaultTemplate({ type: TemplateType_Slug.MANUAL_PAYMENT_REQUEST });
  const hasFF = !!featureFlags[groupId]?.get(PaymentsFeatureFlags.PaymentLinkMessaging)?.value;
  const showButton = hasFF && !!personId && !disabled;
  const { setSelectedMessageTemplateType, setSelectedMessageTemplate } = useTemplateMessagingStore(
    'setSelectedMessageTemplateType',
    'setSelectedMessageTemplate'
  );

  const handleSelectPaymentsTemplate = useCallback(
    (template?: Template) => {
      setSelectedMessageTemplateType?.(TemplateType_Slug.MANUAL_PAYMENT_REQUEST);
      setSelectedMessageTemplate?.(template);
    },
    [setSelectedMessageTemplateType, setSelectedMessageTemplate]
  );

  return {
    popoverItem: showButton
      ? {
          iconName: 'dollar-sign',
          label: t('Payment Request Template'),
          trackingId: `${InboxPrefixes.Thread}-payment-reminder-button`,
          onClick: () => {
            handleSelectPaymentsTemplate(lastUsedPaymentsTemplate);
            modalControl.openModal();
          },
        }
      : undefined,
    tabItem: {
      tabLabel: t('Payments'),
      disabled: !showButton,
      disabledLabel: disabledMessage || t('Contact Not Saved'),
      templateSelectorProps: {
        groupIds: [groupId],
        templateTypes: [TemplateType_Slug.MANUAL_PAYMENT_REQUEST],
        invoiceSelectionTrackingPrefix: IntegratedMessaingTrackingPrefixes.InvoiceSelector,
        groupId,
        onSelectTemplate: (template: Template) => {
          handleSelectPaymentsTemplate(template);
        },
        // We're not returning the button that triggers this modal if there's no personId
        personId: personId ?? '',
        onInsert: ({ invoiceId, textToPayLink, template }) => {
          const resolvedTemplate = template ?? lastUsedPaymentsTemplate;
          if (resolvedTemplate) {
            const relatedType = Object.keys(TEMPLATE_TYPE_LINK_MAP).includes(resolvedTemplate.templateTypeSlug)
              ? TEMPLATE_TYPE_LINK_MAP[resolvedTemplate.templateTypeSlug as keyof typeof TEMPLATE_TYPE_LINK_MAP]
                  ?.relatedType
              : undefined;
            renderTemplate({
              template: resolvedTemplate,
              linkData: [{ link: textToPayLink, relatedId: invoiceId, relatedType }],
              additionalBindings: {
                [DynamicFieldProperty_Enum.TEXT_TO_PAY_LINK]: textToPayLink,
              },
            });
            modalControl.closeModal();
          }
        },
      },
    },
  };
};
